import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { useAuth } from "../context/AuthContext";
import { WhatsappLogo, ArrowsClockwise, Copy, Eye, EyeSlash, CheckCircle, XCircle, Warning, MagnifyingGlass, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { STATUSES } from "../lib/constants";
import usePaginatedLeads from "../lib/usePaginatedLeads";

const STATUS_COUNTS = ["qualified", "appointment_booked", "not_interested", "converted", "out_of_location"];

const statusColor = (s) => ({
  ok: "#008A00",
  created: "#008A00",
  updated: "#002FA7",
  auth_failed: "#E00000",
  validation_failed: "#FF9800",
  bad_json: "#FF9800",
  bad_payload: "#FF9800",
  error: "#E00000",
}[s] || "#52525B");

const statusIcon = (s) => {
  if (s === "ok") return CheckCircle;
  if (s === "auth_failed" || s === "error") return XCircle;
  return Warning;
};

export default function Kraya() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [info, setInfo] = useState(null);
  const [logs, setLogs] = useState([]);
  const [agents, setAgents] = useState([]);
  const [reveal, setReveal] = useState(false);
  const [sideLoading, setSideLoading] = useState(true);

  const initialFilter = {
    status: "", search: "", phone: "", email: "",
    date_from: "", date_to: "", last_event_type: "",
  };
  const [filter, setFilter] = useState(initialFilter);

  const agentMap = useMemo(() => Object.fromEntries(agents.map((a) => [a.id, a.name])), [agents]);

  const buildParams = () => {
    const p = { source: "kraya_whatsapp" };
    Object.entries(filter).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) p[k] = v;
    });
    return p;
  };

  // Progressive pagination — no leads hidden. First page = 3000; user can
  // click "Load more" to append additional 3000-row pages until all rows load.
  const {
    leads, total, loading: leadsLoading, hasMore, isLoadingMore, loadMore,
    reload: reloadLeads,
  } = usePaginatedLeads(buildParams, [JSON.stringify(filter)]);

  const loading = leadsLoading || sideLoading;

  const loadSide = async () => {
    setSideLoading(true);
    try {
      const [w, lg, ag] = await Promise.all([
        // Admin-only — webhook config + secret. Skip fetch for agents (would 403).
        isAdmin ? api.get(`/webhooks/info${reveal ? "?reveal=true" : ""}`) : Promise.resolve({ data: null }),
        isAdmin ? api.get("/webhooks/kraya/logs", { params: { limit: 20 } }) : Promise.resolve({ data: { logs: [] } }),
        agents.length ? Promise.resolve({ data: agents }) : api.get("/agents"),
      ]);
      setInfo(w.data);
      setLogs(lg.data.logs);
      setAgents(ag.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSideLoading(false);
  };

  const load = async () => {
    await Promise.all([loadSide(), reloadLeads()]);
  };

  useEffect(() => { loadSide(); /* eslint-disable-next-line */ }, [reveal]);

  const exportCsv = async (includeFilters = true) => {
    const params = new URLSearchParams();
    if (includeFilters) {
      Object.entries(buildParams()).forEach(([k, v]) => params.append(k, String(v)));
    } else {
      params.append("source", "kraya_whatsapp");
    }
    try {
      const res = await api.get(`/leads/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kraya_${includeFilters ? "filtered" : "all"}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(includeFilters ? "Filtered CSV downloaded" : "All Kraya leads downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const stats = useMemo(() => {
    const s = { total: leads.length };
    STATUS_COUNTS.forEach((k) => { s[k] = 0; });
    let converted = 0;
    for (const l of leads) {
      if (STATUS_COUNTS.includes(l.status)) s[l.status] += 1;
      if (["converted", "booked", "existing_client"].includes(l.status)) converted += 1;
    }
    s.converted = converted;
    s.conv_rate = leads.length ? +((converted / leads.length) * 100).toFixed(1) : 0;
    return s;
  }, [leads]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="WhatsApp AI"
        title="Kraya"
        description="Lead create / update events from Kraya WhatsApp AI. Configure the webhook below and watch live deliveries."
        actions={
          <>
            <button onClick={() => exportCsv(true)} data-testid="kraya-export-filtered"
              className="px-4 py-2.5 text-sm font-semibold border border-border hover:bg-secondary rounded-sm flex items-center gap-2">
              <DownloadSimple size={16} /> Export filtered
            </button>
            <button onClick={() => exportCsv(false)} data-testid="kraya-export-all"
              className="px-4 py-2.5 text-sm font-semibold border border-border hover:bg-secondary rounded-sm flex items-center gap-2">
              <DownloadSimple size={16} /> Export all
            </button>
            <button onClick={load} data-testid="kraya-refresh"
              className="px-4 py-2.5 text-sm font-semibold border border-border hover:bg-secondary rounded-sm flex items-center gap-2">
              <ArrowsClockwise size={16} /> Refresh
            </button>
          </>
        }
      />

      {/* Filters */}
      <div className="px-8 pt-6 flex flex-wrap gap-2 items-center" data-testid="kraya-filters">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            placeholder="Search name / phone / email"
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            data-testid="kraya-search"
            className="text-sm border border-border pl-9 pr-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none w-64"
          />
        </div>
        <input
          placeholder="Phone contains"
          value={filter.phone}
          onChange={(e) => setFilter({ ...filter, phone: e.target.value })}
          data-testid="kraya-filter-phone"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none w-40"
        />
        <input
          placeholder="Email contains"
          value={filter.email}
          onChange={(e) => setFilter({ ...filter, email: e.target.value })}
          data-testid="kraya-filter-email"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none w-44"
        />
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          data-testid="kraya-filter-status"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <input
          placeholder="Event type"
          value={filter.last_event_type}
          onChange={(e) => setFilter({ ...filter, last_event_type: e.target.value })}
          data-testid="kraya-filter-event"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none w-40"
        />
        <input type="date" value={filter.date_from} onChange={(e) => setFilter({ ...filter, date_from: e.target.value })}
          data-testid="kraya-filter-from"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none" />
        <span className="text-muted-foreground text-xs">→</span>
        <input type="date" value={filter.date_to} onChange={(e) => setFilter({ ...filter, date_to: e.target.value })}
          data-testid="kraya-filter-to"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none" />
        {Object.values(filter).some(Boolean) && (
          <button
            onClick={() => setFilter(initialFilter)}
            data-testid="kraya-filter-clear"
            className="text-xs uppercase tracking-[0.15em] font-bold text-primary hover:underline"
          >
            Clear
          </button>
        )}
        <span className="ml-auto text-xs tabular-nums text-muted-foreground" data-testid="kraya-count">
          {leads.length.toLocaleString()} of {total.toLocaleString()} lead{total !== 1 && "s"}
        </span>
      </div>

      {/* KPI strip */}
      <div className="px-8 pt-6 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="kraya-kpis">
        <Kpi label="Total Kraya leads" value={total} color="#002FA7" testid="kpi-total" />
        <Kpi label="Qualified" value={stats.qualified} color="#008A00" testid="kpi-qualified" />
        <Kpi label="Appointment booked" value={stats.appointment_booked} color="#002FA7" testid="kpi-appt" />
        <Kpi label="Not interested" value={stats.not_interested} color="#E00000" testid="kpi-noint" />
        <Kpi label="Conversion" value={`${stats.conv_rate}%`} color="#008A00" testid="kpi-rate" />
      </div>

      {/* Leads table — every WhatsApp conversation that landed as a CRM lead */}
      <div className="px-8 pt-6" data-testid="kraya-leads-section">
        <h2 className="font-display text-base font-bold tracking-tight mb-3">
          WhatsApp leads ({leads.length.toLocaleString()} of {total.toLocaleString()})
        </h2>
        <div className="bg-white border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="kraya-leads-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                <th className="px-4 py-3 font-bold">Customer</th>
                <th className="px-4 py-3 font-bold">Phone</th>
                <th className="px-4 py-3 font-bold">Status</th>
                <th className="px-4 py-3 font-bold">Agent</th>
                <th className="px-4 py-3 font-bold">Treatment</th>
                <th className="px-4 py-3 font-bold">City</th>
                <th className="px-4 py-3 font-bold">Last event</th>
                <th className="px-4 py-3 font-bold">Captured</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-muted-foreground">Loading…</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center">
                  <div className="text-sm text-muted-foreground mb-2">No Kraya leads match these filters.</div>
                  <div className="text-xs text-muted-foreground">
                    Configure the webhook below to start ingesting WhatsApp conversations.
                  </div>
                </td></tr>
              ) : leads.map((l) => (
                <tr key={l.id} className="border-b border-border last:border-0 hover:bg-secondary/40"
                  data-testid={`kraya-lead-row-${l.id}`}>
                  <td className="px-4 py-2.5 font-medium">
                    <Link to={`/leads/${l.id}`} className="hover:text-primary hover:underline"
                      data-testid={`kraya-lead-link-${l.id}`}>
                      {l.customer_name || "—"}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground tabular-nums font-mono text-xs">{l.phone || "—"}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={l.status} /></td>
                  <td className="px-4 py-2.5 text-xs">{agentMap[l.agent_id] || "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{l.metadata?.treatment || "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{l.metadata?.city || "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{l.last_event_type || "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                    {l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <div className="mt-3 flex items-center justify-center">
            <button
              onClick={loadMore}
              disabled={isLoadingMore}
              data-testid="kraya-load-more"
              className="px-6 py-2.5 border-2 border-primary text-primary text-xs uppercase tracking-[0.15em] font-bold hover:bg-primary hover:text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoadingMore
                ? "Loading…"
                : `Load more · ${(total - leads.length).toLocaleString()} remaining`}
            </button>
          </div>
        )}
      </div>

      {/* Webhook config — admin only */}
      {isAdmin && info?.kraya && (
        <div className="px-8 pt-6" data-testid="kraya-config">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-base font-bold tracking-tight">Webhook configuration</h2>
            <button onClick={() => setReveal(!reveal)} data-testid="kraya-reveal-toggle"
              className="text-xs uppercase tracking-[0.15em] font-bold text-primary hover:underline flex items-center gap-1">
              {reveal ? <EyeSlash size={14} /> : <Eye size={14} />}
              {reveal ? "Mask" : "Reveal"} secret
            </button>
          </div>
          <div className="bg-white border border-border p-5 space-y-3">
            <Field label="Endpoint URL" value={info.kraya.url} onCopy={() => copy(info.kraya.url, "URL")} testid="kraya-url" />
            <Field label="Auth header name" value={info.kraya.auth_header} onCopy={() => copy(info.kraya.auth_header, "Header name")} testid="kraya-header-name" />
            <Field label="Auth header value" value={info.kraya.auth_value} mono onCopy={() => copy(info.kraya.auth_value, "Header value")}
              warn={info.kraya.auth_masked} testid="kraya-header-value" />
            <details className="pt-2">
              <summary className="text-xs uppercase tracking-[0.15em] font-bold cursor-pointer text-muted-foreground hover:text-primary">
                Sample payload
              </summary>
              <pre className="mt-2 p-3 bg-secondary text-xs font-mono overflow-x-auto" data-testid="kraya-sample-payload">
                {JSON.stringify(info.kraya.sample_payload, null, 2)}
              </pre>
            </details>
            <details>
              <summary className="text-xs uppercase tracking-[0.15em] font-bold cursor-pointer text-muted-foreground hover:text-primary">
                Stage → status mapping
              </summary>
              <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1">
                {Object.entries(info.kraya.stage_map || {}).map(([k, v]) => (
                  <div key={k} className="text-xs flex items-center justify-between border-b border-border py-1">
                    <span className="font-mono">{k}</span>
                    <span className="text-muted-foreground">→</span>
                    <span className="font-bold" style={{ color: "#002FA7" }}>{v}</span>
                  </div>
                ))}
              </div>
            </details>
          </div>
        </div>
      )}

      {/* Recent webhook deliveries — admin only */}
      {isAdmin && (
      <div className="px-8 pt-6 pb-8" data-testid="kraya-logs">
        <h2 className="font-display text-base font-bold tracking-tight mb-3">
          Last 20 webhook deliveries
        </h2>
        <div className="bg-white border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                <th className="px-4 py-3 font-bold w-44">When</th>
                <th className="px-4 py-3 font-bold w-28">Status</th>
                <th className="px-4 py-3 font-bold w-24">Action</th>
                <th className="px-4 py-3 font-bold">Phone</th>
                <th className="px-4 py-3 font-bold">Event</th>
                <th className="px-4 py-3 font-bold">Lead</th>
                <th className="px-4 py-3 font-bold">IP / Error</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground">Loading…</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-16 text-center">
                  <div className="text-sm text-muted-foreground mb-2">No webhook deliveries yet.</div>
                  <div className="text-xs text-muted-foreground">
                    Paste the URL above into Kraya&apos;s webhook config to start seeing events here.
                  </div>
                </td></tr>
              ) : logs.map((log, i) => {
                const Icon = statusIcon(log.processing_status);
                return (
                  <tr key={i} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`kraya-log-row-${i}`}>
                    <td className="px-4 py-2.5 text-xs tabular-nums text-muted-foreground whitespace-nowrap">
                      {new Date(log.at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5" style={{ color: statusColor(log.processing_status) }}>
                        <Icon size={14} weight="bold" />
                        <span className="text-[10px] uppercase tracking-[0.1em] font-bold">{log.processing_status}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs uppercase tracking-[0.1em] font-bold"
                      style={{ color: log.action ? statusColor(log.action) : "#c4c4cc" }}>
                      {log.action || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-xs font-mono tabular-nums">{log.phone || "—"}</td>
                    <td className="px-4 py-2.5 text-xs">{log.event_type || "—"}</td>
                    <td className="px-4 py-2.5 text-xs">
                      {log.lead_id && log.action ? (
                        <Link to={`/leads/${log.lead_id}`} className="text-primary hover:underline">
                          open ↗
                        </Link>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-[260px] truncate"
                      title={log.error_message || log.ip || ""}>
                      {log.error_message ? <span style={{ color: "#E00000" }}>{log.error_message}</span> : log.ip || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}

function Kpi({ label, value, color, testid }) {
  return (
    <div className="border border-border bg-white p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">{label}</div>
      <div className="font-display text-3xl font-bold tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}

function Field({ label, value, mono, onCopy, warn, testid }) {
  return (
    <div className="grid grid-cols-[180px_1fr_auto] gap-3 items-center" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-bold">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""} ${warn ? "text-muted-foreground italic" : ""} truncate`}>
        {value}
      </div>
      <button onClick={onCopy} className="p-1.5 hover:bg-secondary border border-border" title="Copy">
        <Copy size={14} />
      </button>
    </div>
  );
}
